import { useState, useEffect, useRef, useMemo } from 'react';
import './AnimatedHeroText.css';

// Separate component for each character to handle proximity effect
function AnimatedChar({ char, index, lineIndex, isAnimated, mousePosition, containerRef, isMouseActive, activeLineIndex }) {
  const charRef = useRef(null);
  const [style, setStyle] = useState({});
  const positionRef = useRef({ x: 0, y: 0 });

  // Cache character position on mount and when animated
  useEffect(() => {
    if (!charRef.current || !containerRef.current) return;

    const updatePosition = () => {
      const charRect = charRef.current.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();
      
      positionRef.current = {
        x: charRect.left - containerRect.left + charRect.width / 2,
        y: charRect.top - containerRect.top + charRect.height / 2,
      };
    };

    updatePosition();
    
    // Update on window resize
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [isAnimated, containerRef]);

  // Calculate scale based on mouse position
  useEffect(() => {
    // Hanya aktifkan efek jika mouse ada di baris yang sama
    const isLineActive = activeLineIndex === lineIndex;
    
    if (!isAnimated || !isMouseActive || !isLineActive) {
      setStyle({
        animationDelay: `${index * 0.03}s`,
        transform: isAnimated ? 'scale(1)' : 'translateY(100%)',
        textShadow: 'none',
        filter: 'brightness(1)',
      });
      return;
    }

    const centerX = positionRef.current.x;
    const centerY = positionRef.current.y;
    
    const distance = Math.sqrt(
      Math.pow(mousePosition.x - centerX, 2) + 
      Math.pow(mousePosition.y - centerY, 2)
    );
    
    // Max distance for effect (in pixels) - lebih kecil untuk efek lebih fokus
    const maxDistance = 120;
    const normalizedDistance = Math.min(distance / maxDistance, 1);
    
    // Scale dari 1 ke 2.2 - SANGAT dramatis
    const scale = 1 + (1 - normalizedDistance) * 1.2;
    
    // Intensity untuk efek cahaya
    const intensity = (1 - normalizedDistance);
    
    // Multiple shadows untuk efek glow yang kuat
    const glowStrength = intensity * 40;
    const textShadow = intensity > 0.2 
      ? `
        0 0 ${glowStrength}px rgba(6, 182, 212, ${intensity * 0.9}),
        0 0 ${glowStrength * 1.5}px rgba(6, 182, 212, ${intensity * 0.7}),
        0 0 ${glowStrength * 2}px rgba(6, 182, 212, ${intensity * 0.5}),
        0 0 ${glowStrength * 3}px rgba(59, 130, 246, ${intensity * 0.4})
      `
      : 'none';
    
    // Brightness boost untuk karakter yang dekat
    const brightness = 1 + (intensity * 0.5);
    
    setStyle({
      animationDelay: `${index * 0.03}s`,
      transform: `scale(${scale}) translateZ(0)`,
      textShadow,
      filter: `brightness(${brightness})`,
      color: intensity > 0.5 ? `rgba(6, 182, 212, ${0.8 + intensity * 0.2})` : 'inherit',
    });
  }, [mousePosition, isAnimated, index, isMouseActive, activeLineIndex, lineIndex]);

  return (
    <span
      ref={charRef}
      className={`animated-char ${isAnimated ? 'animated' : ''}`}
      style={style}
    >
      {char === ' ' ? '\u00A0' : char}
    </span>
  );
}

export default function AnimatedHeroText({ 
  line1 = "Web & IoT Engineering Studio",
  line2 = "untuk Mahasiswa & UMKM"
}) {
  const [isAnimated, setIsAnimated] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isMouseActive, setIsMouseActive] = useState(false);
  const [activeLineIndex, setActiveLineIndex] = useState(null);
  const containerRef = useRef(null);
  const line1Ref = useRef(null);
  const line2Ref = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    // Trigger split animation after mount
    const timer = setTimeout(() => {
      setIsAnimated(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const handleMouseMove = (e) => {
    if (!isAnimated || !containerRef.current) return;
    
    // Cancel previous animation frame
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    
    // Use requestAnimationFrame for smooth updates
    rafRef.current = requestAnimationFrame(() => {
      const rect = containerRef.current.getBoundingClientRect();
      const mouseY = e.clientY;
      
      // Deteksi baris mana yang sedang di-hover
      let hoveredLine = null;
      
      if (line1Ref.current) {
        const line1Rect = line1Ref.current.getBoundingClientRect();
        if (mouseY >= line1Rect.top && mouseY <= line1Rect.bottom) {
          hoveredLine = 0;
        }
      }
      
      if (line2Ref.current) {
        const line2Rect = line2Ref.current.getBoundingClientRect();
        if (mouseY >= line2Rect.top && mouseY <= line2Rect.bottom) {
          hoveredLine = 1;
        }
      }
      
      setActiveLineIndex(hoveredLine);
      setMousePosition({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsMouseActive(true);
    });
  };

  const handleMouseEnter = () => {
    if (isAnimated) {
      setIsMouseActive(true);
    }
  };

  const handleMouseLeave = () => {
    setIsMouseActive(false);
    setActiveLineIndex(null);
    setMousePosition({ x: 0, y: 0 });
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
  };

  const renderAnimatedText = (text, lineIndex) => {
    return text.split('').map((char, index) => (
      <AnimatedChar
        key={`${lineIndex}-${index}`}
        char={char}
        index={index}
        lineIndex={lineIndex}
        isAnimated={isAnimated}
        mousePosition={mousePosition}
        containerRef={containerRef}
        isMouseActive={isMouseActive}
        activeLineIndex={activeLineIndex}
      />
    ));
  };

  return (
    <div 
      ref={containerRef}
      className="animated-hero-text"
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="hero-title">
        <div ref={line1Ref} className="text-line">
          {renderAnimatedText(line1, 0)}
        </div>
        <div ref={line2Ref} className="text-line gradient-line">
          {renderAnimatedText(line2, 1)}
        </div>
      </div>
    </div>
  );
}
